import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import Seo from '../components/Seo';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen pt-20 bg-gray-50">
      <Seo
        title="Privacy Policy | MoveraCare Mobility"
        description="MoveraCare Mobility privacy policy: how we collect, use, and safeguard personal and health-related information through our medical transportation services."
        path="/privacy"
        noindex
      />
      {/* Header */}
      <section className="bg-[#004C97] py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center text-white hover:text-[#F9A825] transition-colors mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <div className="flex items-center space-x-4 mb-4">
            <Shield className="w-12 h-12 text-[#F9A825]" />
            <h1 className="text-3xl md:text-4xl font-bold text-white">Privacy Policy</h1>
          </div>
          <p className="text-gray-200">
            <strong>Effective Date:</strong> April 25, 2026 | <strong>Last Updated:</strong> April 25, 2026
          </p>
        </div>
      </section>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-md p-8 space-y-8">
          <div>
            <p className="text-gray-700 leading-relaxed">
              MoveraCare Services LLC ("<strong>MoveraCare</strong>," "we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard personal and health-related information through our website and service platforms.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-[#004C97] mb-4 flex items-center">
              <Lock className="w-6 h-6 mr-2 text-[#F9A825]" />
              1. Information We Collect
            </h2>
            <p className="text-gray-700 mb-4">
              We collect information necessary to coordinate medical transportation, including:
            </p>
            <ul className="space-y-3 ml-6">
              <li className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-[#F9A825] rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <strong className="text-[#004C97]">Personal Identifiers:</strong>
                  <span className="text-gray-700"> Name, phone number, email, and physical address.</span>
                </div>
              </li>
              <li className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-[#F9A825] rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <strong className="text-[#004C97]">Health Information:</strong>
                  <span className="text-gray-700"> Pick-up requirements (e.g., wheelchair/ambulatory), appointment times, and facility destinations.</span>
                </div>
              </li>
              <li className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-[#F9A825] rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <strong className="text-[#004C97]">Technical Data:</strong>
                  <span className="text-gray-700"> IP address and browser type for security and site optimization.</span>
                </div>
              </li>
            </ul>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-[#004C97] mb-4">2. How We Use Your Information</h2>
            <p className="text-gray-700 mb-4">We use your data strictly for:</p>
            <ul className="space-y-2 ml-6">
              <li className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-[#F9A825] rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-gray-700">Scheduling and confirming transportation.</span>
              </li>
              <li className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-[#F9A825] rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-gray-700">Invoicing and insurance billing.</span>
              </li>
              <li className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-[#F9A825] rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-gray-700">Improving our service safety and reliability.</span>
              </li>
            </ul>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-[#004C97] mb-4">3. Data Security</h2>
            <p className="text-gray-700">
              We implement industry-standard encryption and physical safeguards. Access to your Protected Health Information (PHI) is restricted to employees who require it to perform their duties (the "Minimum Necessary" standard).
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-[#004C97] mb-4">4. Your Rights</h2>
            <p className="text-gray-700 mb-4">Under applicable privacy laws, you have the right to:</p>
            <ul className="space-y-2 ml-6">
              <li className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-[#F9A825] rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-gray-700">Access and request a copy of your personal information</span>
              </li>
              <li className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-[#F9A825] rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-gray-700">Request correction of inaccurate information</span>
              </li>
              <li className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-[#F9A825] rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-gray-700">Request deletion of your information (subject to legal requirements)</span>
              </li>
              <li className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-[#F9A825] rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-gray-700">Receive notification in the event of a data breach</span>
              </li>
            </ul>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-[#004C97] mb-4">5. Third-Party Sharing</h2>
            <p className="text-gray-700">
              We do not sell your personal information. We may share data with healthcare facilities, insurance brokers, and payment processors solely for service delivery and billing purposes.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-[#004C97] mb-4">6. Contact Us</h2>
            <p className="text-gray-700 mb-4">
              For privacy inquiries or to exercise your rights, please contact us:
            </p>
            <div className="bg-gray-50 p-6 rounded-lg border-2 border-[#F9A825]">
              <p className="font-semibold text-[#004C97] mb-2">MoveraCare Services LLC</p>
              <p className="text-gray-700">Privacy Officer</p>
              <div className="flex items-center space-x-2 text-gray-700 mt-3">
                <Phone className="w-4 h-4 text-[#F9A825]" />
                <span>(832) 925-2128</span>
              </div>
              <p className="text-gray-700 mt-2">Email: privacy@moveracaremobility.com</p>
            </div>
          </div>

          <div className="border-t pt-6">
            <p className="text-sm text-gray-600">
              This Privacy Policy may be updated periodically. We will notify you of material changes by posting the updated policy on our website with a new "Last Updated" date.
            </p>
          </div>
        </div>

        <div className="mt-8 text-center">
          <Link to="/">
            <Button className="bg-[#004C97] text-white hover:bg-[#003D7A]">
              Return to Homepage
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;